import { Link } from "react-router-dom";
import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import {
  AdminCard,
  EmptyInline,
  MetricCard,
  Pill,
  QuickActionTile,
  RailCard,
  RailKv,
  RailProgress,
  Row,
} from "../components/dashboard";
import { Icon } from "../components/icons";
import { usePlatformStatus } from "../hooks/usePlatformStatus";
import { useSession } from "../hooks/useSession";
import { useAppContext } from "../hooks/useAppContext";
import { demoMode } from "../lib/config";

/**
 * Overview — a faithful implementation of Figma "03 — Dashboard". Layout,
 * components and styling match the design exactly; values are driven by the
 * live control API + session with honest empty states. No mockup number
 * (Galactic Legends, 92/100, 4,812 …) is baked in.
 */

const INTEGRATIONS = [
  { name: "Discord", mark: "D" },
  { name: "Twitch", mark: "T" },
  { name: "YouTube", mark: "Y" },
  { name: "X (Twitter)", mark: "X" },
  { name: "Google Sheets", mark: "G" },
] as const;

const QUICK_ACTIONS = [
  { icon: <Icon.tickets />, label: "Create Ticket", to: "/app/tickets" },
  { icon: <Icon.megaphone />, label: "Send Announcement", to: "/app/content" },
  { icon: <Icon.automations />, label: "Add Automation", to: "/app/automations" },
  { icon: <Icon.roles />, label: "Manage Roles", to: "/app/roles" },
  { icon: <Icon.poll />, label: "Create Poll", to: "/app/content" },
  { icon: <Icon.analytics />, label: "View Analytics", to: "/app/analytics" },
] as const;

export function Overview(): JSX.Element {
  const status = usePlatformStatus();
  const { state: session } = useSession();
  const { tenantName, guildName } = useAppContext();

  const ready = status.kind === "ready" ? status.data : null;
  const connected = Boolean(guildName);
  const foot = connected ? "Awaiting first sync" : "Connect a server";
  // In demo mode we still render the full dashboard chrome (with honest empty
  // values) so the preview shows the real product structure, not a bare notice.
  const showChrome = ready !== null || demoMode;

  return (
    <div className="ax-content">
      <div className="ax-workspace">
        <div className="ax-head">
          <div>
            <h1 className="ax-head__title">Overview</h1>
            <p className="ax-head__sub">A real-time summary of your server, bot and active services.</p>
          </div>
          <Link to="/app/servers" className="ax-btn ax-btn--secondary">
            {connected ? "Customise" : "Connect a server"}
          </Link>
        </div>

        {status.kind === "loading" ? <LoadingState label="Contacting the control API…" /> : null}
        {status.kind === "not-configured" ? (
          <NotConfiguredState title="Control API URL not configured">
            Set <code>PUBLIC_API_URL</code> when building the dashboard to see live status here.
          </NotConfiguredState>
        ) : null}
        {status.kind === "unavailable" ? <ApiUnavailableState detail={status.detail} /> : null}

        {showChrome ? (
          <>
            {ready && !connected ? (
              <div className="ax-notice">
                <strong>No Discord server connected yet</strong>
                <span>
                  The metrics below populate once a server is connected.{" "}
                  <Link to="/app/servers" className="ax-card__link">
                    Connect one to see live numbers
                  </Link>
                  .
                </span>
              </div>
            ) : null}

            <div className="ax-metrics">
              <MetricCard label="Server Health" icon={<Icon.heart />} iconTone="success" value={null} foot={foot} />
              <MetricCard label="Members" icon={<Icon.members />} value={null} foot={foot} />
              <MetricCard label="New Members" icon={<Icon.userPlus />} value={null} foot={foot} />
            </div>
            <div className="ax-metrics">
              <MetricCard label="Open Tickets" icon={<Icon.tickets />} iconTone="warning" value={null} foot={foot} />
              <MetricCard label="Moderation Alerts" icon={<Icon.moderation />} iconTone="error" value={null} foot={foot} />
              <MetricCard label="Scheduled Posts" icon={<Icon.calendar />} value={null} foot={foot} />
            </div>

            <div className="ax-cols">
              <AdminCard title="Integration Status" link={{ label: "View all", to: "/app/integrations" }}>
                {INTEGRATIONS.map((it) => (
                  <Row
                    key={it.name}
                    mark={it.mark}
                    name={it.name}
                    right={<Pill tone="neutral" label="Not connected" />}
                  />
                ))}
              </AdminCard>

              <AdminCard title="Recent Bot Actions" link={{ label: "View logs", to: "/app/logs" }}>
                <EmptyInline title="No actions yet">
                  Bot activity appears here once ODIN starts acting on a connected server.
                </EmptyInline>
              </AdminCard>
            </div>

            <div className="ax-cols">
              <AdminCard
                title="AI Recommendations"
                link={{ label: "Open BALDER", to: "/app/balder" }}
              >
                <EmptyInline title="Recommendations need a connected server">
                  Once a server is connected, ODIN suggests concrete next steps here. You can also ask
                  BALDER to plan a change at any time.
                </EmptyInline>
              </AdminCard>

              <AdminCard title="Quick Actions">
                <div className="ax-qa">
                  {QUICK_ACTIONS.map((qa) => (
                    <QuickActionTile key={qa.label} icon={qa.icon} label={qa.label} to={qa.to} />
                  ))}
                </div>
              </AdminCard>
            </div>
          </>
        ) : null}
      </div>

      <aside className="ax-rail">
        <RailCard title="Server & Bot">
          <RailKv k="Server" v={guildName ?? <span style={{ color: "var(--text-tertiary)" }}>Not connected</span>} />
          <RailKv k="Workspace" v={tenantName ?? <span style={{ color: "var(--text-tertiary)" }}>Not selected</span>} />
          <RailKv
            k="Staging bot"
            v={
              ready ? (
                <Pill tone={ready.discordStagingConfigured ? "success" : "neutral"} label={ready.discordStagingConfigured ? "Configured" : "Not set"} />
              ) : (
                "—"
              )
            }
          />
          <div className="ax-rail-actions">
            <Link to="/app/servers" className="ax-btn ax-btn--secondary ax-btn--block">
              Manage servers
            </Link>
          </div>
        </RailCard>

        <RailCard title="Platform">
          <RailKv k="Environment" v={ready ? ready.environment : "—"} />
          <RailKv
            k="Live deployment"
            v={ready ? <Pill tone={ready.liveDeploymentLocked ? "success" : "warning"} label={ready.liveDeploymentLocked ? "Locked (safe)" : "Enabled"} /> : "—"}
          />
          <RailKv
            k="Dry run"
            v={ready ? <Pill tone={ready.safety.dryRun ? "success" : "warning"} label={ready.safety.dryRun ? "On" : "Off"} /> : "—"}
          />
          <RailKv
            k="Require approval"
            v={ready ? <Pill tone={ready.safety.requireApproval ? "success" : "error"} label={ready.safety.requireApproval ? "On" : "Off"} /> : "—"}
          />
        </RailCard>

        <RailCard title="Subscription">
          <RailKv k="Plan" v={<span style={{ color: "var(--text-tertiary)" }}>Not available</span>} />
          <span className="ax-metric__foot">Billing details appear here once a plan is active.</span>
          <div className="ax-rail-actions">
            <Link to="/app/billing" className="ax-btn ax-btn--secondary ax-btn--block">
              View billing
            </Link>
          </div>
        </RailCard>

        <RailCard title="Session">
          <RailKv k="Signed in as" v={session.kind === "authenticated" ? (session.me.displayName ?? session.me.username) : "—"} />
          <RailKv k="Role" v={session.kind === "authenticated" ? session.me.platformRole : "—"} />
          <div className="ax-rail-actions">
            <Link to="/app/settings" className="ax-btn ax-btn--secondary ax-btn--block">
              Account settings
            </Link>
          </div>
        </RailCard>

        <RailCard title="Permissions">
          <RailKv k="Bot permissions" v={<span style={{ color: "var(--text-tertiary)" }}>—</span>} />
          <RailProgress percent={null} />
          <span className="ax-metric__foot">Shown after a server snapshot is taken.</span>
          <div className="ax-rail-actions">
            <Link to="/app/snapshot" className="ax-btn ax-btn--secondary ax-btn--block">
              Take a snapshot
            </Link>
          </div>
        </RailCard>
      </aside>
    </div>
  );
}
